import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import Divider from '@mui/material/Divider';
import { getRdv } from '../services/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const PROXY_URL = 'https://kortex.phone.gs/vtigercrm/moncrm/agendaEspagne/api/apidistance.php';

const DistanceManagementPage = () => {
  const { t } = useTranslation();

  const [rdvs, setRdvs] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [origineFilter, setOrigineFilter] = useState(''); // Filtre Origine
  const [filialeFilter, setFilialeFilter] = useState(''); // Filtre Filiale
  const [data, setData] = useState([]); // Résultat final par équipe
  const [availabilitySummary, setAvailabilitySummary] = useState([]);
  const [dailyCount, setDailyCount] = useState(0);
  const [filterLoading, setFilterLoading] = useState(false);

  // Options pour les picklists
  const [origineOptions, setOrigineOptions] = useState([]);
  const [filialeOptions, setFilialeOptions] = useState([]);
  const [equipeOptions, setEquipeOptions] = useState([]);

  // Pour récupérer les infos des équipes (point de départ, zones, etc.)
  const [equipesData, setEquipesData] = useState([]);

  // Récupérer les RDV depuis l'API au montage du composant
  useEffect(() => {
    const fetchRdvs = async () => {
      try {
        const data = await getRdv();
        setRdvs(data);
        // Extraire les valeurs uniques pour les picklists et trier alphabétiquement
        const origines = [...new Set(data.map(rdv => rdv.Origine).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b));
        const filiales = [...new Set(data.map(rdv => rdv.filiale).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b));
        const equipes = [...new Set(data.map(rdv => rdv.equipe).filter(Boolean))]
          .sort((a, b) => {
            // Extraire les numéros des équipes
            const numA = parseInt(a.replace('EQUIPE ', ''));
            const numB = parseInt(b.replace('EQUIPE ', ''));
            return numA - numB;
          });
        setOrigineOptions(origines);
        setFilialeOptions(filiales);
        setEquipeOptions(equipes);
      } catch (error) {
        console.error(t('distanceManagement.fetchRdvsError', { defaultValue: "Erreur lors de la récupération des RDV:" }), error);
      }
    };
    fetchRdvs();
  }, [t]);

  // Récupérer les informations des équipes depuis la nouvelle API
  useEffect(() => {
    const fetchEquipes = async () => {
      try {
        const response = await axios.get('https://kortex.phone.gs/vtigercrm/moncrm/agendaEspagne/api/getEquipes.php');
        setEquipesData(response.data);
      } catch (error) {
        console.error(t('distanceManagement.fetchEquipesError', { defaultValue: "Erreur lors de la récupération des équipes:" }), error);
      }
    };
    fetchEquipes();
  }, [t]);

  const handleFilter = async () => {
    setFilterLoading(true);
    // Filtrer par date, équipe, origine et filiale
    const filtered = rdvs.filter(rdv => {
      const matchDate = selectedDate ? rdv.rdv_date === selectedDate : true;
      const matchTeam = teamFilter ? (rdv.equipe || '').toLowerCase() === teamFilter.toLowerCase() : true;
      const matchOrigine = origineFilter ? rdv.Origine === origineFilter : true;
      const matchFiliale = filialeFilter ? rdv.filiale === filialeFilter : true;
      return matchDate && matchTeam && matchOrigine && matchFiliale;
    });
    setDailyCount(filtered.length);

    // Groupement par équipe
    const groups = {};
    filtered.forEach(rdv => {
      const team = rdv.equipe || t('distanceManagement.noTeam', { defaultValue: "Sans équipe" });
      if (!groups[team]) groups[team] = [];
      groups[team].push(rdv);
    });

    // Calcul du récapitulatif de disponibilité
    const summary = [];
    for (const team in groups) {
      const count = groups[team].length;
      if (count < 3) {
        const availableSlots = 3 - count;
        const lastAppointment = groups[team][groups[team].length - 1];
        summary.push({
          team,
          availableSlots,
          lastCp: lastAppointment ? lastAppointment.cp : t('distanceManagement.undefinedCp', { defaultValue: "N/A" }),
        });
      }
    }
    setAvailabilitySummary(summary);

    // Tri par heure dans chaque groupe
    for (const team in groups) {
      groups[team].sort((a, b) =>
        new Date(`${a.rdv_date}T${a.rdv_time}`) - new Date(`${b.rdv_date}T${b.rdv_time}`)
      );
    }

    // Pour chaque groupe, calculer distance et durée entre RDV consécutifs
    // et calculer également la distance du point de départ vers le premier RDV
    const result = [];
    for (const team in groups) {
      const teamAppointments = groups[team];
      const teamResults = [];

      // Calcul de la distance du point de départ vers le premier RDV
      let departDistance = '';
      let departDuration = '';
      if (equipesData.length > 0 && teamAppointments.length > 0) {
        const equipeObj = equipesData.find(e => e.equipe === team);
        if (equipeObj && equipeObj.depart) {
          try {
            const response = await axios.get(PROXY_URL, {
              params: {
                origins: equipeObj.depart,
                destinations: teamAppointments[0].address,
                mode: 'driving',
              },
            });
            if (
              response.data &&
              response.data.rows &&
              response.data.rows[0] &&
              response.data.rows[0].elements[0] &&
              response.data.rows[0].elements[0].status === 'OK'
            ) {
              departDistance = response.data.rows[0].elements[0].distance.text;
              departDuration = response.data.rows[0].elements[0].duration.text;
            }
          } catch (error) {
            console.error(t('distanceManagement.departError', { defaultValue: "Erreur lors du calcul de distance depuis le départ :" }), error);
          }
        }
      }

      // Pour chaque groupe, calculer la distance entre rendez‑vous consécutifs
      for (let i = 0; i < teamAppointments.length; i++) {
        const current = teamAppointments[i];
        let nextDistance = '';
        let nextDuration = '';
        let nextDurationValue = null;
        let timeDiffHours = null;
        let alert5h = false;
        if (i < teamAppointments.length - 1) {
          const next = teamAppointments[i + 1];
          try {
            const response = await axios.get(PROXY_URL, {
              params: {
                origins: current.address,
                destinations: next.address,
                mode: 'driving',
              },
            });
            if (
              response.data &&
              response.data.rows &&
              response.data.rows[0] &&
              response.data.rows[0].elements[0] &&
              response.data.rows[0].elements[0].status === 'OK'
            ) {
              nextDistance = response.data.rows[0].elements[0].distance.text;
              nextDuration = response.data.rows[0].elements[0].duration.text;
              nextDurationValue = response.data.rows[0].elements[0].duration.value; // en secondes
            }
          } catch (error) {
            console.error(t('distanceManagement.proxyError', { defaultValue: "Erreur lors de l'appel du proxy Distance:" }), error);
          }
          // Calcul de l'intervalle (en heures) entre current et next
          const currentTime = new Date(`${current.rdv_date}T${current.rdv_time}`);
          const nextTime = new Date(`${next.rdv_date}T${next.rdv_time}`);
          timeDiffHours = (nextTime - currentTime) / 3600000;
          if (parseFloat(current.surface) > 200 && timeDiffHours < 5) {
            alert5h = true;
          }
        }
        teamResults.push({ ...current, nextDistance, nextDuration, nextDurationValue, timeDiffHours, alert5h });
      }
      result.push({ team, departDistance, departDuration, appointments: teamResults });
    }
    setData(result);
    setFilterLoading(false);
  };

  // Récupérer la liste des équipes distinctes (pour le picklist du filtre) parmi tous les RDV
  const distinctTeams = Array.from(new Set(rdvs.map(rdv => rdv.equipe).filter(Boolean)));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('distanceManagement.title', { defaultValue: "Gestion des distances et disponibilités" })}
      </Typography>

      {/* Récapitulatif des disponibilités */}
      {selectedDate && availabilitySummary.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Paper sx={{ p: 2, backgroundColor: '#e3f2fd' }}>
            <Typography variant="h6" gutterBottom>
              {t('distanceManagement.summaryTitle', { defaultValue: "Disponibilités pour le" })} {selectedDate} :
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {availabilitySummary.map((item, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Paper sx={{ p: 1, textAlign: 'center' }} elevation={3}>
                    <Typography variant="subtitle1" color="primary">
                      {t('distanceManagement.teamLabel', { defaultValue: "Équipe" })} {item.team}
                    </Typography>
                    <Typography variant="body1">
                      {t('distanceManagement.availableSlots', { defaultValue: "Créneaux disponibles" })} : {item.availableSlots}
                    </Typography>
                    <Typography variant="body1">
                      {t('distanceManagement.lastCp', { defaultValue: "Dernier RDV au CP" })} : {item.lastCp}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
      )}

      {filterLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Zone de filtres */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          label={t('filters.date', { defaultValue: "Date (AAAA-MM-JJ)" })}
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="team-filter-label">{t('filters.team', { defaultValue: "Équipe" })}</InputLabel>
          <Select
            labelId="team-filter-label"
            value={teamFilter}
            label={t('filters.team', { defaultValue: "Équipe" })}
            onChange={(e) => setTeamFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>{t('filters.all', { defaultValue: "Toutes" })}</em>
            </MenuItem>
            {distinctTeams.map(team => (
              <MenuItem key={team} value={team}>{team}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="origine-filter-label">{t('filters.origin', { defaultValue: "Origine" })}</InputLabel>
          <Select
            labelId="origine-filter-label"
            value={origineFilter}
            label={t('filters.origin', { defaultValue: "Origine" })}
            onChange={(e) => setOrigineFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>{t('filters.all', { defaultValue: "Toutes" })}</em>
            </MenuItem>
            {origineOptions.map(origine => (
              <MenuItem key={origine} value={origine}>{origine}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="filiale-filter-label">{t('filters.branch', { defaultValue: "Filiale" })}</InputLabel>
          <Select
            labelId="filiale-filter-label"
            value={filialeFilter}
            label={t('filters.branch', { defaultValue: "Filiale" })}
            onChange={(e) => setFilialeFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>{t('filters.all', { defaultValue: "Toutes" })}</em>
            </MenuItem>
            {filialeOptions.map(filiale => (
              <MenuItem key={filiale} value={filiale}>{filiale}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleFilter}>
          {t('filters.apply', { defaultValue: "Appliquer filtre" })}
        </Button>
        <Button variant="outlined" onClick={() => {
          setSelectedDate('');
          setTeamFilter('');
          setOrigineFilter('');
          setFilialeFilter('');
        }}>
          {t('filters.reset', { defaultValue: "Réinitialiser" })}
        </Button>
      </Box>

      {/* Affichage par groupe d'équipe */}
      {data.map(group => (
        <Paper key={group.team} sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('distanceManagement.teamLabel', { defaultValue: "Équipe" })} : {group.team}
          </Typography>
          {group.departDistance && (
            <Box sx={{ mb: 1, backgroundColor: '#f0f4c3', p: 1, borderRadius: 1 }}>
              <Typography variant="subtitle2">
                {t('distanceManagement.departureDistance', { defaultValue: "Distance du point de départ vers le premier RDV :" })}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {group.departDistance}
              </Typography>
              <Typography variant="body2">
                {t('distanceManagement.duration', { defaultValue: "Durée" })} : {group.departDuration}
              </Typography>
            </Box>
          )}
          {group.appointments.length > 3 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {t('distanceManagement.appointmentsWarning', { defaultValue: "Attention : {0} rendez‑vous programmés pour l'équipe", 0: group.appointments.length })} {group.team}.
            </Alert>
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('table.column.client', { defaultValue: "Prénom" })}</TableCell>
                  <TableCell>{t('table.column.client', { defaultValue: "Nom" })}</TableCell>
                  <TableCell>{t('distanceManagement.dateLabel', { defaultValue: "Date" })}</TableCell>
                  <TableCell>{t('distanceManagement.timeLabel', { defaultValue: "Heure" })}</TableCell>
                  <TableCell>{t('table.column.address', { defaultValue: "Adresse" })}</TableCell>
                  <TableCell>{t('table.column.estimatedSurface', { defaultValue: "Surface estimée" })}</TableCell>
                  <TableCell>{t('table.column.installedSurface', { defaultValue: "Surface installée" })}</TableCell>
                  <TableCell>{t('table.column.origin', { defaultValue: "Origine" })}</TableCell>
                  <TableCell>{t('table.column.installationType', { defaultValue: "Type install" })}</TableCell>
                  <TableCell>{t('table.column.branch', { defaultValue: "Filiale" })}</TableCell>
                  <TableCell>{t('distanceManagement.nextDistance', { defaultValue: "Distance vers le suivant" })}</TableCell>
                  <TableCell>{t('distanceManagement.nextDuration', { defaultValue: "Durée vers le suivant" })}</TableCell>
                  <TableCell>{t('distanceManagement.alert5h', { defaultValue: "Alerte 5h" })}</TableCell>
                  <TableCell>{t('table.column.action', { defaultValue: "Action" })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.appointments.map((rdv, idx) => {
                  const rowStyle =
                    rdv.nextDurationValue && rdv.nextDurationValue > 3600
                      ? { backgroundColor: 'rgba(255,165,0,0.3)' }
                      : {};
                  return (
                    <TableRow key={idx} style={rowStyle}>
                      <TableCell>{rdv.Prenom}</TableCell>
                      <TableCell>{rdv.Nom}</TableCell>
                      <TableCell>{rdv.rdv_date}</TableCell>
                      <TableCell>{rdv.rdv_time}</TableCell>
                      <TableCell>{rdv.address}</TableCell>
                      <TableCell>{rdv.surface_estime || "-"}</TableCell>
                      <TableCell>{rdv.surface_installe || "-"}</TableCell>
                      <TableCell>{rdv.Origine || "-"}</TableCell>
                      <TableCell>{rdv.type_install || "-"}</TableCell>
                      <TableCell>{rdv.filiale || "-"}</TableCell>
                      <TableCell>{rdv.nextDistance}</TableCell>
                      <TableCell>{rdv.nextDuration}</TableCell>
                      <TableCell>
                        {rdv.alert5h && (
                          <Typography variant="caption" color="error">
                            {t('distanceManagement.alertText', { defaultValue: "5h requises" })}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {rdv.leadid && (
                          <Button variant="contained" color="primary" href={`https://kortex.phone.gs/vtigercrm/index.php?module=Leads&view=Detail&record=${rdv.leadid}`} target="_blank">
                            {t('dialog.accessFile', { defaultValue: "Accès fiche" })}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}
    </Box>
  );
};

export default DistanceManagementPage;
